import type {
  ApiResponse,
  Patient,
  Physio,
  Cancellation,
  Suggestion,
  Appointment,
  ImportResult,
  SyncStatus,
  CreatePhysioInput,
  UpdatePhysioInput,
  CreateCancellationInput,
  UpdateEnrichmentInput,
  PatientEnrichment,
  RequirementTag,
  RequirementTagWithCapabilities,
  CreateRequirementTagInput,
  UpdateRequirementTagInput,
  RankingCriterion,
  UpdateRankingCriteriaInput,
  PollSummary,
  PollCreateResponse,
  CreatePollInput,
  PollRespondInfo,
  PollSharedRespondInfo,
  PollRespondRequest,
  PhysioStatusInfo,
  PhysioStatusLink,
  PhysioStatus,
} from '../types';

const API_BASE = process.env.REACT_APP_API_URL || 'https://physio-scheduler-api.onrender.com/api';

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('authToken');
  if (token) {
    return { Authorization: `Bearer ${token}` };
  }
  return {};
}

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const url = `${API_BASE}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...options.headers,
    },
  });

  // Handle 401 - redirect to login
  if (response.status === 401) {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    window.location.href = '/login';
    throw new Error('Session expired. Please log in again.');
  }

  const data = (await response.json()) as ApiResponse<T>;

  if (!response.ok) {
    throw new Error(data.error?.message || 'API request failed');
  }

  return data;
}

// Patients API
export const patientsApi = {
  getAll: () => fetchApi<Patient[]>('/patients'),

  getById: (id: number) => fetchApi<Patient>(`/patients/${id}`),

  getEnrichment: (id: number) => fetchApi<PatientEnrichment>(`/patients/${id}/enrichment`),

  updateEnrichment: (id: number, data: UpdateEnrichmentInput) =>
    fetchApi<PatientEnrichment>(`/patients/${id}/enrichment`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  importCsv: async (file: File): Promise<ApiResponse<ImportResult>> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE}/patients/import`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
      window.location.href = '/login';
      throw new Error('Session expired. Please log in again.');
    }

    return response.json() as Promise<ApiResponse<ImportResult>>;
  },
};

// Physios API
export const physiosApi = {
  getAll: () => fetchApi<Physio[]>('/physios'),

  getById: (id: number) => fetchApi<Physio>(`/physios/${id}`),

  create: (data: CreatePhysioInput) =>
    fetchApi<Physio>('/physios', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: number, data: UpdatePhysioInput) =>
    fetchApi<Physio>(`/physios/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: number) =>
    fetchApi<{ message: string }>(`/physios/${id}`, {
      method: 'DELETE',
    }),

  // Status management
  getStatusLinks: () => fetchApi<PhysioStatusLink[]>('/physios/status-links'),

  generateStatusToken: (id: number) =>
    fetchApi<{ physio_id: number; physio_name: string; status_token: string; status_link: string }>(
      `/physios/${id}/generate-status-token`,
      { method: 'POST' }
    ),
};

// Physio Status API (public - no auth)
export const physioStatusApi = {
  // Get list of physio names for self-identification
  getPhysioNames: () =>
    fetchApi<Array<{ id: number; name: string }>>('/physio-status/lookup/names'),

  // Look up physio by ID and get/generate their status token
  lookupPhysio: (id: number) =>
    fetchApi<{ physio_id: number; physio_name: string; status: PhysioStatus; status_token: string; updated_at: string }>(
      `/physio-status/lookup/${id}`
    ),

  getStatus: (statusToken: string) =>
    fetchApi<PhysioStatusInfo>(`/physio-status/${statusToken}`),

  updateStatus: (statusToken: string, status: PhysioStatus) =>
    fetchApi<PhysioStatusInfo>(`/physio-status/${statusToken}`, {
      method: 'POST',
      body: JSON.stringify({ status }),
    }),

  // Get team status (public)
  getTeamStatus: () =>
    fetchApi<Array<{ id: number; name: string; status: PhysioStatus; updated_at: string | null }>>('/physio-status/team'),
};

// Cancellations API
export const cancellationsApi = {
  getAll: (status?: string) =>
    fetchApi<Cancellation[]>(`/cancellations${status ? `?status=${status}` : ''}`),

  getById: (id: number) => fetchApi<Cancellation>(`/cancellations/${id}`),

  create: (data: CreateCancellationInput) =>
    fetchApi<Cancellation>('/cancellations', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateStatus: (id: number, status: 'pending' | 'resolved' | 'failed') =>
    fetchApi<Cancellation>(`/cancellations/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    }),

  getSuggestions: (id: number) => fetchApi<Suggestion[]>(`/cancellations/${id}/suggestions`),

  regenerateSuggestions: (id: number) =>
    fetchApi<Suggestion[]>(`/cancellations/${id}/suggestions/regenerate`, {
      method: 'POST',
    }),

  confirmSuggestion: (cancellationId: number, suggestionId: number) =>
    fetchApi<{
      appointment_id: number;
      cancellation_id: number;
      physio_id: number;
      physio_name: string;
      patient_name: string;
      scheduled_time: string;
    }>(`/cancellations/${cancellationId}/suggestions/${suggestionId}/confirm`, {
      method: 'POST',
    }),
};

// Appointments API
export const appointmentsApi = {
  getAll: (params?: { status?: string; upcoming?: boolean }) => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append('status', params.status);
    if (params?.upcoming) searchParams.append('upcoming', 'true');
    const queryString = searchParams.toString();
    return fetchApi<Appointment[]>(`/appointments${queryString ? `?${queryString}` : ''}`);
  },

  getById: (id: number) => fetchApi<Appointment>(`/appointments/${id}`),

  importCsv: async (file: File): Promise<ApiResponse<ImportResult>> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE}/appointments/import`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
      window.location.href = '/login';
      throw new Error('Session expired. Please log in again.');
    }

    return response.json() as Promise<ApiResponse<ImportResult>>;
  },
};

// Admin API
export const adminApi = {
  getSyncStatus: () => fetchApi<SyncStatus>('/admin/sync-status'),

  triggerSync: () =>
    fetchApi<{ message: string }>('/admin/sync-now', {
      method: 'POST',
    }),

  seedDatabase: () =>
    fetchApi<{ physios: number; patients: number; appointments: number }>('/admin/seed', {
      method: 'POST',
    }),

  calculateDistance: (origin: string, destination: string) =>
    fetchApi<{ distance_km: number; duration_mins: number }>('/distance', {
      method: 'POST',
      body: JSON.stringify({
        origin_postcode: origin,
        destination_postcode: destination,
      }),
    }),

  geocode: (postcode: string) =>
    fetchApi<{ lat: number; lng: number }>('/geocode', {
      method: 'POST',
      body: JSON.stringify({ postcode }),
    }),

  geocodeBatch: (postcodes: string[]) =>
    fetchApi<Record<string, { lat: number; lng: number } | null>>('/geocode/batch', {
      method: 'POST',
      body: JSON.stringify({ postcodes }),
    }),

  getRoute: (points: Array<{ lat: number; lng: number }>) =>
    fetchApi<{ coordinates: Array<[number, number]>; distance_km: number; duration_mins: number }>('/route', {
      method: 'POST',
      body: JSON.stringify({ points }),
    }),
};

// Requirement Tags API
export const requirementTagsApi = {
  getAll: () => fetchApi<RequirementTagWithCapabilities[]>('/requirement-tags'),

  getById: (id: number) => fetchApi<RequirementTagWithCapabilities>(`/requirement-tags/${id}`),

  create: (data: CreateRequirementTagInput) =>
    fetchApi<RequirementTagWithCapabilities>('/requirement-tags', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: number, data: UpdateRequirementTagInput) =>
    fetchApi<RequirementTagWithCapabilities>(`/requirement-tags/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: number) =>
    fetchApi<{ message: string }>(`/requirement-tags/${id}`, {
      method: 'DELETE',
    }),

  // Patient requirements
  getPatientRequirements: (patientId: number) =>
    fetchApi<RequirementTag[]>(`/requirement-tags/patient/${patientId}`),

  addPatientRequirement: (patientId: number, tagId: number) =>
    fetchApi<{ message: string }>(`/requirement-tags/patient/${patientId}`, {
      method: 'POST',
      body: JSON.stringify({ requirement_tag_id: tagId }),
    }),

  removePatientRequirement: (patientId: number, tagId: number) =>
    fetchApi<{ message: string }>(`/requirement-tags/patient/${patientId}/${tagId}`, {
      method: 'DELETE',
    }),
};

// Polls API
export const pollsApi = {
  create: (data: CreatePollInput) =>
    fetchApi<PollCreateResponse>('/polls', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getAll: (page = 1, limit = 10) =>
    fetchApi<PollSummary[]>(`/polls?page=${page}&limit=${limit}`),

  getById: (id: number) => fetchApi<PollSummary>(`/polls/${id}`),

  getSharedRespondInfo: (pollToken: string) =>
    fetchApi<PollSharedRespondInfo>(`/polls/respond/${pollToken}`),

  getRespondInfo: (pollToken: string, responseToken: string) =>
    fetchApi<PollRespondInfo>(`/polls/respond/${pollToken}/${responseToken}`),

  submitResponse: (pollToken: string, responseToken: string, data: PollRespondRequest) =>
    fetchApi<{ response: 'in' | 'out'; responded_at: string }>(
      `/polls/respond/${pollToken}/${responseToken}`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    ),
};

// Ranking Criteria API
export const rankingCriteriaApi = {
  getAll: () => fetchApi<RankingCriterion[]>('/ranking-criteria'),

  update: (data: UpdateRankingCriteriaInput) =>
    fetchApi<RankingCriterion[]>('/ranking-criteria', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  reset: () =>
    fetchApi<RankingCriterion[]>('/ranking-criteria/reset', {
      method: 'POST',
    }),
};

// SMS API
export const smsApi = {
  getStatus: () =>
    fetchApi<{ configured: boolean; message: string }>('/sms/status'),

  sendTest: (phone: string) =>
    fetchApi<{ message: string }>('/sms/test', {
      method: 'POST',
      body: JSON.stringify({ phone }),
    }),

  broadcast: () =>
    fetchApi<{ sent: number; failed: number; skipped: number; message: string }>('/sms/broadcast', {
      method: 'POST',
    }),
};
